import React, { useRef, useState } from "react";
import {
  LuAlertCircle,
  LuCheckCircle2,
  LuFileSpreadsheet,
  LuUpload,
  LuX,
} from "react-icons/lu";
import { toast } from "react-toastify";

import { GET, POSTFormData } from "../../services/axios.service";
import { openDownload } from "../../services/download.service";
import { toastStyles } from "../../static/styles";
import {
  ImportResult,
  ImportRowError,
  ImportRowName,
} from "../../types/common";
import Button from "../ui/Button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "../ui/Dialog";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  dialogMobileBodyClass,
  dialogMobileContentClass,
  dialogMobileFooterClass,
  dialogMobileFormClass,
  dialogMobileHeaderClass,
  dialogTitleClass,
  fieldLabelClass,
} from "../ui/styles";

const MAX_FILE_MB = 2;
const EXTENSIONS = ["xlsx", "csv"];
// File type checks go by extension: browsers report CSV files as text/csv, application/vnd.ms-excel or nothing.
const ACCEPT =
  ".xlsx,.csv,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
const FALLBACK_MESSAGE = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  // The module's API path, e.g. "/v1/admin/categories".
  endpoint: string;
  help: string[];
  onImported: () => void;
};

const listBoxClass = "max-h-40 overflow-y-auto space-y-1 text-sm";

const ImportModal = ({
  open,
  onClose,
  title,
  endpoint,
  help,
  onImported,
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [sampleFormat, setSampleFormat] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [errors, setErrors] = useState<ImportRowError[]>([]);
  const [skipped, setSkipped] = useState<ImportRowName[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [resultMessage, setResultMessage] = useState<string>("");

  const clearOutcome = () => {
    setFailure(null);
    setErrors([]);
    setSkipped([]);
    setResult(null);
  };

  const handleClose = () => {
    if (isUploading) return;

    setFile(null);
    clearOutcome();
    onClose();
  };

  const selectFile = (selected: File) => {
    const extension = selected.name.split(".").pop()?.toLowerCase() || "";

    if (!EXTENSIONS.includes(extension)) {
      toast.error("ફક્ત Excel (.xlsx) અથવા CSV (.csv) ફાઇલ પસંદ કરો.", {
        ...(toastStyles as any),
      });
      return;
    }

    if (selected.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`ફાઇલ ${MAX_FILE_MB} MB થી મોટી છે.`, {
        ...(toastStyles as any),
      });
      return;
    }

    clearOutcome();
    setFile(selected);
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) selectFile(selected);
    // Lets the same file be picked again after it is fixed.
    event.target.value = "";
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    const selected = event.dataTransfer.files[0];
    if (selected) selectFile(selected);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDropZoneKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const handleSampleDownload = async (format: string) => {
    setSampleFormat(format);

    try {
      const response: any = await GET(`${endpoint}/import-sample`, { format });

      if (response?.data?.status === "success") {
        openDownload(response.data.data.download_link);
      } else {
        toast.error(response?.data?.message || FALLBACK_MESSAGE, {
          ...(toastStyles as any),
        });
      }
    } catch (error) {
      toast.error(FALLBACK_MESSAGE, { ...(toastStyles as any) });
    } finally {
      setSampleFormat(null);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setIsUploading(true);
    clearOutcome();

    try {
      const response: any = await POSTFormData(`${endpoint}/import`, { file });
      const body = response?.data;

      if (body?.status === "success") {
        setFile(null);
        setResult({
          created: body.data?.created ?? 0,
          assigned: body.data?.assigned ?? [],
          skipped: body.data?.skipped ?? [],
          updated: body.data?.updated ?? [],
        });
        setResultMessage(body.message);
        onImported();

        toast.success(body.message, { ...(toastStyles as any) });
      } else {
        // A server limit (e.g. a 413 page) has no JSON message.
        setFailure(
          typeof body?.message === "string" ? body.message : FALLBACK_MESSAGE
        );
        setErrors(Array.isArray(body?.data?.errors) ? body.data.errors : []);
        setSkipped(Array.isArray(body?.data?.skipped) ? body.data.skipped : []);
      }
    } catch (error) {
      setFailure(FALLBACK_MESSAGE);
    } finally {
      setIsUploading(false);
    }
  };

  const renderSkipped = (rows: ImportRowName[]) =>
    rows.length > 0 && (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
        <p className="mb-2 text-sm font-medium">
          પહેલેથી ઉમેરેલ હોવાથી છોડી દીધેલ પંક્તિઓ:
        </p>
        <ul className={listBoxClass}>
          {rows.map((row) => (
            <li key={`${row.row}-${row.name}`}>
              પંક્તિ {row.row}: {row.name}
            </li>
          ))}
        </ul>
      </div>
    );

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent
        size="lg"
        allowClose={!isUploading}
        className={cx(
          "font-inter max-h-[90vh] overflow-y-auto",
          dialogMobileContentClass
        )}
      >
        <DialogHeader className={dialogMobileHeaderClass}>
          <h2 className={dialogTitleClass}>{title}</h2>
        </DialogHeader>

        <div className={cx("space-y-4", dialogMobileFormClass)}>
          <div className={cx("space-y-4", dialogMobileBodyClass)}>
            {result ? (
              <>
                <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                  <LuCheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{resultMessage}</span>
                </div>

                {result.assigned.length > 0 && (
                  <div className="rounded-lg border p-3">
                    <p className="mb-2 text-sm font-medium">
                      કોડ ખાલી હતો એટલે આપમેળે બનેલા કોડ:
                    </p>
                    <ul className={listBoxClass}>
                      {result.assigned.map((row) => (
                        <li key={row.row}>
                          પંક્તિ {row.row}: {row.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.updated.length > 0 && (
                  <div className="rounded-lg border p-3">
                    <p className="mb-2 text-sm font-medium">
                      ફાઇલ મુજબ સુધારેલી પંક્તિઓ:
                    </p>
                    <ul className={listBoxClass}>
                      {result.updated.map((row) => (
                        <li key={row.row}>
                          પંક્તિ {row.row}: {row.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {renderSkipped(result.skipped)}
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <span className={fieldLabelClass}>સેમ્પલ ફાઇલ</span>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={sampleFormat !== null}
                      onClick={() => handleSampleDownload("xlsx")}
                    >
                      <LuFileSpreadsheet className="h-4 w-4" />
                      {sampleFormat === "xlsx" ? "બની રહી છે..." : "Excel"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={sampleFormat !== null}
                      onClick={() => handleSampleDownload("csv")}
                    >
                      <LuFileSpreadsheet className="h-4 w-4" />
                      {sampleFormat === "csv" ? "બની રહી છે..." : "CSV"}
                    </Button>
                  </div>
                </div>

                <ul className="list-disc space-y-1 pl-5 text-sm text-gray-600">
                  {help.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                  <li>
                    ગુજરાતી લખાણ માટે Excel (.xlsx) વાપરો. CSV હોય તો Excel માં
                    "CSV UTF-8" તરીકે સેવ કરો.
                  </li>
                  <li>
                    એક વખતમાં વધુમાં વધુ 1000 પંક્તિ; ભૂલ વાળી પંક્તિ હોય તો
                    કંઈ જ સેવ નહીં થાય.
                  </li>
                </ul>

                {file ? (
                  <div className="flex items-center gap-3 rounded-lg border-2 border-gray-200 p-3">
                    <LuFileSpreadsheet className="h-6 w-6 shrink-0 text-[#113F67]" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="iconSm"
                      title="દૂર કરો"
                      disabled={isUploading}
                      onClick={() => {
                        setFile(null);
                        clearOutcome();
                      }}
                    >
                      <LuX className="h-4 w-4" />
                      <span className="sr-only">દૂર કરો</span>
                    </Button>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={openFilePicker}
                    onKeyDown={handleDropZoneKeyDown}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={() => setIsDragOver(false)}
                    className={cx(
                      "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#113F67] focus-visible:ring-offset-2",
                      isDragOver
                        ? "border-[#113F67] bg-[#113F67]/5"
                        : "border-gray-300 hover:border-[#113F67] hover:bg-gray-50"
                    )}
                  >
                    <LuUpload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      ફાઇલ અહીં ડ્રોપ કરો અથવા પસંદ કરવા ક્લિક કરો
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Excel (.xlsx), CSV (.csv) · {MAX_FILE_MB} MB સુધી
                    </p>
                  </div>
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={handleFileChange}
                  className="hidden"
                />

                {failure && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                    <p className="flex items-start gap-2 text-sm font-medium">
                      <LuAlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>{failure}</span>
                    </p>
                    {errors.length > 0 && (
                      <ul className={cx(listBoxClass, "mt-2")}>
                        {errors.map((error) => (
                          <li key={error.row}>
                            પંક્તિ {error.row}: {error.message}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {renderSkipped(skipped)}
              </>
            )}
          </div>

          <DialogFooter className={dialogMobileFooterClass}>
            {result ? (
              <Button
                type="button"
                variant="primary"
                className="flex-1"
                onClick={handleClose}
              >
                બંધ કરો
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  રદ કરો
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  className="flex-1"
                  onClick={handleImport}
                  disabled={!file || isUploading}
                >
                  {isUploading ? (
                    <span className="flex items-center">
                      <span className={buttonSpinnerClass}></span>
                      ઇમ્પોર્ટ થઈ રહ્યું છે...
                    </span>
                  ) : (
                    "ઇમ્પોર્ટ કરો"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportModal;
